import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWorld } from "../state/World";
import { api } from "../lib/api";

// Founder-only in-app doc viewer. Activate by appending `?docs=1` to any URL.
// Reads /app/memory/*.md via /api/admin/docs. Not a router change — an overlay.

export default function DocsOverlay() {
  const { me } = useWorld();
  const location = useLocation();

  const active = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("docs") === "1";
  }, [location.search]);

  const isFounder = me?.user?.role === "founder";

  const [tiers, setTiers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Squawk composer state — the drop-a-thought affordance.
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTarget, setComposerTarget] = useState("SQUAWK_LIST.md");
  const [composerLabel, setComposerLabel] = useState("");
  const [composerBody, setComposerBody] = useState("");
  const [composerSaving, setComposerSaving] = useState(false);
  const [composerMsg, setComposerMsg] = useState("");

  // Fetch the tiered file list on activation.
  useEffect(() => {
    if (!active || !isFounder || tiers) return;
    setErr("");
    api
      .get("/admin/docs")
      .then(({ data }) => {
        setTiers(data.tiers || {});
        const first =
          data.tiers?.["1 · Read first"]?.find((d) => d.name === "README.md") ||
          Object.values(data.tiers || {}).flat()[0];
        if (first) setSelected(first.path);
      })
      .catch((e) => setErr(e?.response?.data?.detail || String(e)));
  }, [active, isFounder, tiers]);

  // Fetch selected doc content. Bumping `reloadKey` re-fetches after an append.
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!active || !isFounder || !selected) return;
    setLoading(true);
    setErr("");
    api
      .get("/admin/docs/content", { params: { path: selected } })
      .then(({ data }) => setContent(data.content || ""))
      .catch((e) => setErr(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false));
  }, [active, isFounder, selected, reloadKey]);

  const submitSquawk = async () => {
    if (!composerBody.trim()) return;
    setComposerSaving(true);
    setComposerMsg("");
    try {
      await api.post("/admin/docs/append", {
        path: composerTarget,
        entry: composerBody,
        label: composerLabel.trim() || null,
      });
      setComposerMsg("Dropped.");
      setComposerBody("");
      setComposerLabel("");
      // If we're currently viewing the target file, refresh it.
      if (selected === composerTarget) setReloadKey((k) => k + 1);
      setTimeout(() => setComposerMsg(""), 2200);
    } catch (e) {
      setComposerMsg(
        "Failed: " + (e?.response?.data?.detail || String(e))
      );
    } finally {
      setComposerSaving(false);
    }
  };

  // Esc closes.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        const q = new URLSearchParams(location.search);
        q.delete("docs");
        const suffix = q.toString();
        window.history.replaceState(
          null,
          "",
          location.pathname + (suffix ? `?${suffix}` : "")
        );
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, location]);

  if (!active) return null;

  // Silent no-op for non-founders. Ceremony: the overlay simply isn't there.
  if (!isFounder) return null;

  const isJson = selected?.endsWith(".json");

  return (
    <div
      data-testid="docs-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "grid",
        gridTemplateColumns: "300px 1fr",
        background: "rgba(8, 6, 4, 0.94)",
        backdropFilter: "blur(18px)",
        color: "#f2e7d3",
        fontFamily: "Outfit, sans-serif",
      }}
    >
      {/* Sidebar — tiered reading order */}
      <aside
        style={{
          borderRight: "1px solid rgba(224,164,74,0.18)",
          overflowY: "auto",
          padding: "20px 16px 40px",
          background: "rgba(0,0,0,0.35)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontSize: 22,
                color: "#e0a44a",
                letterSpacing: 1.5,
              }}
            >
              Architect's Book
            </div>
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2 }}>
              memory/ — founder view
            </div>
          </div>
          <button
            data-testid="docs-overlay-close"
            onClick={() => {
              const q = new URLSearchParams(location.search);
              q.delete("docs");
              const suffix = q.toString();
              window.history.replaceState(
                null,
                "",
                location.pathname + (suffix ? `?${suffix}` : "")
              );
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
            style={{
              background: "transparent",
              border: "1px solid rgba(224,164,74,0.35)",
              color: "#e0a44a",
              padding: "4px 10px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 11,
              letterSpacing: 1,
            }}
            aria-label="Close docs (Esc)"
          >
            ESC
          </button>
        </div>

        {err && (
          <div
            style={{
              color: "#ff9b7a",
              fontSize: 12,
              padding: 10,
              border: "1px solid rgba(255,155,122,0.3)",
              borderRadius: 4,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}

        {!tiers && !err && (
          <div style={{ opacity: 0.5, fontSize: 12 }}>Reading the shelf…</div>
        )}

        {tiers &&
          Object.entries(tiers).map(([label, rows]) => (
            <div key={label} style={{ marginBottom: 22 }}>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 1.8,
                  textTransform: "uppercase",
                  opacity: 0.55,
                  marginBottom: 8,
                  color: "#e0a44a",
                }}
              >
                Tier {label}
              </div>
              {rows.map((row) => {
                const isSelected = selected === row.path;
                return (
                  <button
                    key={row.path}
                    data-testid={`docs-file-${row.name}`}
                    onClick={() => setSelected(row.path)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "6px 8px",
                      marginBottom: 2,
                      background: isSelected
                        ? "rgba(224,164,74,0.14)"
                        : "transparent",
                      border: "none",
                      borderLeft: isSelected
                        ? "2px solid #e0a44a"
                        : "2px solid transparent",
                      color: isSelected ? "#e0a44a" : "#f2e7d3",
                      fontFamily: "inherit",
                      fontSize: 12.5,
                      cursor: "pointer",
                      borderRadius: 2,
                    }}
                  >
                    {row.name}
                  </button>
                );
              })}
            </div>
          ))}

        {/* Squawk / drop-a-thought composer. Sits at the tail of the shelf. */}
        <div
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: "1px solid rgba(224,164,74,0.14)",
          }}
        >
          {!composerOpen ? (
            <button
              data-testid="docs-squawk-open"
              onClick={() => setComposerOpen(true)}
              style={{
                width: "100%",
                padding: "10px 12px",
                background: "rgba(224,164,74,0.06)",
                border: "1px solid rgba(224,164,74,0.28)",
                color: "#e0a44a",
                fontFamily: "inherit",
                fontSize: 12.5,
                letterSpacing: 1,
                cursor: "pointer",
                borderRadius: 4,
                textAlign: "left",
              }}
            >
              + Drop a Squawk
            </button>
          ) : (
            <div>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 1.8,
                  textTransform: "uppercase",
                  opacity: 0.55,
                  marginBottom: 8,
                  color: "#e0a44a",
                }}
              >
                Drop into
              </div>
              <select
                data-testid="docs-squawk-target"
                value={composerTarget}
                onChange={(e) => setComposerTarget(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(224,164,74,0.28)",
                  color: "#f2e7d3",
                  fontFamily: "inherit",
                  fontSize: 12.5,
                  marginBottom: 10,
                  borderRadius: 3,
                }}
              >
                <option value="SQUAWK_LIST.md">SQUAWK_LIST.md</option>
                <option value="OPEN_ITEMS.md">OPEN_ITEMS.md</option>
              </select>

              <input
                data-testid="docs-squawk-label"
                type="text"
                value={composerLabel}
                onChange={(e) => setComposerLabel(e.target.value)}
                placeholder="Short title (optional)"
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(224,164,74,0.20)",
                  color: "#f2e7d3",
                  fontFamily: "inherit",
                  fontSize: 12.5,
                  marginBottom: 8,
                  borderRadius: 3,
                }}
              />

              <textarea
                data-testid="docs-squawk-body"
                value={composerBody}
                onChange={(e) => setComposerBody(e.target.value)}
                placeholder="The squawk — one line or several…"
                rows={5}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(224,164,74,0.20)",
                  color: "#f2e7d3",
                  fontFamily: "inherit",
                  fontSize: 12.5,
                  marginBottom: 10,
                  borderRadius: 3,
                  resize: "vertical",
                }}
              />

              <div style={{ display: "flex", gap: 6 }}>
                <button
                  data-testid="docs-squawk-submit"
                  onClick={submitSquawk}
                  disabled={composerSaving || !composerBody.trim()}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    background: composerBody.trim()
                      ? "rgba(224,164,74,0.18)"
                      : "rgba(224,164,74,0.06)",
                    border: "1px solid rgba(224,164,74,0.4)",
                    color: "#e0a44a",
                    fontFamily: "inherit",
                    fontSize: 12,
                    letterSpacing: 1,
                    cursor: composerBody.trim() ? "pointer" : "not-allowed",
                    borderRadius: 3,
                  }}
                >
                  {composerSaving ? "Dropping…" : "Drop it"}
                </button>
                <button
                  data-testid="docs-squawk-cancel"
                  onClick={() => {
                    setComposerOpen(false);
                    setComposerBody("");
                    setComposerLabel("");
                    setComposerMsg("");
                  }}
                  style={{
                    padding: "8px 12px",
                    background: "transparent",
                    border: "1px solid rgba(224,164,74,0.2)",
                    color: "#f2e7d3",
                    fontFamily: "inherit",
                    fontSize: 12,
                    cursor: "pointer",
                    borderRadius: 3,
                  }}
                >
                  Cancel
                </button>
              </div>

              {composerMsg && (
                <div
                  data-testid="docs-squawk-msg"
                  style={{
                    marginTop: 8,
                    fontSize: 11,
                    color: composerMsg.startsWith("Failed")
                      ? "#ff9b7a"
                      : "#a8d8a2",
                    opacity: 0.85,
                  }}
                >
                  {composerMsg}
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Content pane */}
      <main
        style={{
          overflowY: "auto",
          padding: "36px 48px 80px",
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            fontSize: 12,
            opacity: 0.5,
            letterSpacing: 1.5,
            marginBottom: 6,
          }}
        >
          {selected || "—"}
        </div>
        {loading ? (
          <div style={{ opacity: 0.5, marginTop: 20 }}>Opening the page…</div>
        ) : isJson ? (
          <pre
            style={{
              background: "rgba(0,0,0,0.4)",
              padding: 20,
              borderRadius: 6,
              overflowX: "auto",
              fontSize: 12.5,
              lineHeight: 1.55,
              color: "#f2e7d3",
              border: "1px solid rgba(224,164,74,0.14)",
            }}
          >
            {content}
          </pre>
        ) : (
          <div
            data-testid="docs-overlay-body"
            className="docs-md"
            style={{ maxWidth: 820, lineHeight: 1.65, fontSize: 15 }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
      </main>

      {/* Minimal in-file styling for the markdown. Kept inline so this
          component is self-contained — no CSS file to import. */}
      <style>{`
        .docs-md h1 { font-family: "Cormorant Garamond", serif; font-size: 32px; color: #e0a44a; margin: 0 0 18px; letter-spacing: 1px; }
        .docs-md h2 { font-family: "Cormorant Garamond", serif; font-size: 24px; color: #e0a44a; margin: 32px 0 12px; letter-spacing: 0.5px; }
        .docs-md h3 { font-size: 17px; color: #e0a44a; margin: 24px 0 8px; opacity: 0.9; }
        .docs-md h4 { font-size: 14px; color: #e0a44a; margin: 18px 0 6px; opacity: 0.85; text-transform: uppercase; letter-spacing: 1px; }
        .docs-md p  { margin: 0 0 14px; opacity: 0.92; }
        .docs-md ul, .docs-md ol { margin: 0 0 14px 22px; opacity: 0.92; }
        .docs-md li { margin-bottom: 4px; }
        .docs-md a  { color: #e0a44a; text-decoration: underline; text-underline-offset: 3px; }
        .docs-md strong { color: #f5d998; }
        .docs-md em { color: #d9c3a2; }
        .docs-md hr { border: none; border-top: 1px solid rgba(224,164,74,0.18); margin: 32px 0; }
        .docs-md blockquote { border-left: 2px solid rgba(224,164,74,0.4); padding: 4px 0 4px 16px; margin: 14px 0; opacity: 0.85; font-style: italic; }
        .docs-md code { background: rgba(224,164,74,0.10); padding: 1px 5px; border-radius: 3px; font-size: 13px; color: #f5d998; font-family: "JetBrains Mono", ui-monospace, monospace; }
        .docs-md pre { background: rgba(0,0,0,0.4); padding: 16px 20px; border-radius: 6px; overflow-x: auto; margin: 0 0 16px; border: 1px solid rgba(224,164,74,0.14); }
        .docs-md pre code { background: transparent; padding: 0; color: #f2e7d3; font-size: 12.5px; }
        .docs-md table { border-collapse: collapse; margin: 0 0 18px; font-size: 13px; }
        .docs-md th, .docs-md td { border: 1px solid rgba(224,164,74,0.20); padding: 6px 12px; text-align: left; }
        .docs-md th { background: rgba(224,164,74,0.08); color: #e0a44a; }
      `}</style>
    </div>
  );
}
